/**
 * The editor: toolbar, document, panel.
 *
 * Responsive per 14.3. Above `lg` the panel sits beside the document; below it the
 * panel becomes a tab, because a 320px symbol table next to a 320px document helps
 * nobody. The unverified marker is present at every width and is not something the
 * layout is allowed to drop (1.3.2).
 */
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CircleHelp,
  Image,
  LineChart,
  Lock,
  Plus,
  Redo2,
  Table2,
  Type,
  Undo2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/button'
import { CellRow } from './CellRow'
import { ImageCellBody, MathCellBody, PlotCellBody, TableCellBody, TextCellBody } from './cells'
import { ShortcutsDialog, useShortcutsHotkey } from './ShortcutsDialog'
import { VariablesPanel } from './VariablesPanel'
import type { SheetDetail } from '../api'
import { newCellId } from '../model/document'
import type { Cell, CellId } from '../model/types'
import type { SheetController } from '../useSheet'

type Pane = 'sheet' | 'variables'

interface DragState {
  readonly id: CellId
  readonly to: number
  readonly allowed: boolean
}

export interface SheetEditorProps {
  readonly sheet: SheetController
  /**
   * The server id, needed by image cells to upload and fetch. Omitted, it is read
   * from the `/sheets/:sheetId` route; `null` means the sheet is not saved yet.
   */
  readonly sheetId?: number | null
  /**
   * Whether the caller may change the sheet. Omitted, it is read from the sheet
   * detail the page already fetched (`can_edit`, cached under `['sheet', id]`),
   * so a reader of a workspace-visible sheet is not shown upload controls that
   * the server would only refuse.
   */
  readonly readOnly?: boolean
}

export function SheetEditor({ sheet, sheetId, readOnly }: SheetEditorProps) {
  const { t } = useTranslation('sheets')
  const [pane, setPane] = useState<Pane>('sheet')
  const [drag, setDrag] = useState<DragState | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const openShortcuts = useCallback(() => setShortcutsOpen(true), [])
  useShortcutsHotkey(openShortcuts)

  const params = useParams<{ sheetId: string }>()
  const queryClient = useQueryClient()
  const routeId = Number(params.sheetId)
  const resolvedSheetId =
    sheetId !== undefined ? sheetId : params.sheetId && Number.isFinite(routeId) ? routeId : null
  const cached =
    resolvedSheetId === null
      ? undefined
      : queryClient.getQueryData<SheetDetail>(['sheet', resolvedSheetId])
  const isReadOnly = readOnly ?? (cached ? !cached.can_edit : false)

  const { doc, graph, results, selected } = sheet
  const errorCount = [...results.values()].filter((r) => r.status === 'error').length
  const okCount = [...results.values()].filter((r) => r.status === 'ok').length

  const insertAfterSelected = (cell: Cell) => {
    const at = selected ? doc.cells.findIndex((c) => c.id === selected) + 1 : doc.cells.length
    sheet.insert(cell, at)
  }

  const addMath = () => insertAfterSelected({ id: newCellId(), type: 'math', latex: '' })
  const addText = () => insertAfterSelected({ id: newCellId(), type: 'text', html: '' })
  const addTable = () =>
    insertAfterSelected({
      id: newCellId(),
      type: 'table',
      caption: '',
      rows: [{ symbol: '', value: null, unit: '' }],
    })
  const addPlot = () =>
    insertAfterSelected({
      id: newCellId(),
      type: 'plot',
      caption: '',
      latex: '',
      sweep: 'x',
      from: 0,
      to: 10,
      steps: 25,
    })
  const addImage = () =>
    insertAfterSelected({ id: newCellId(), type: 'image', assetId: null, alt: '' })

  const moveBy = (id: CellId, delta: number) => {
    const from = doc.cells.findIndex((cell) => cell.id === id)
    sheet.move(id, Math.max(0, Math.min(from + delta, doc.cells.length - 1)))
  }

  const renderBody = (cell: Cell) => {
    const result = results.get(cell.id)
    switch (cell.type) {
      case 'math':
        return (
          <MathCellBody
            cell={cell}
            result={result}
            onChange={(latex) => sheet.update({ ...cell, latex })}
            onFocus={() => sheet.select(cell.id)}
          />
        )
      case 'text':
        return <TextCellBody cell={cell} onChange={(html) => sheet.update({ ...cell, html })} />
      case 'table':
        return <TableCellBody cell={cell} onChange={sheet.update} />
      case 'plot':
        return <PlotCellBody cell={cell} result={result} onChange={sheet.update} />
      case 'image':
        return (
          <ImageCellBody
            cell={cell}
            sheetId={resolvedSheetId}
            readOnly={isReadOnly}
            onChange={sheet.update}
          />
        )
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-surface px-4 py-2">
        <Button variant="secondary" size="sm" onClick={addMath}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.insert.math')}
        </Button>
        <Button variant="ghost" size="sm" onClick={addText}>
          <Type className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.insert.text')}
        </Button>
        <Button variant="ghost" size="sm" onClick={addTable}>
          <Table2 className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.insert.table')}
        </Button>
        <Button variant="ghost" size="sm" onClick={addPlot}>
          <LineChart className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.insert.plot')}
        </Button>
        <Button variant="ghost" size="sm" onClick={addImage}>
          <Image className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.insert.image')}
        </Button>

        <span className="mx-2 h-5 w-px bg-border" aria-hidden />

        <Button
          variant="ghost"
          size="iconSm"
          onClick={sheet.undo}
          disabled={!sheet.canUndo}
          aria-label={t('sheets.action.undo')}
        >
          <Undo2 className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          variant="ghost"
          size="iconSm"
          onClick={sheet.redo}
          disabled={!sheet.canRedo}
          aria-label={t('sheets.action.redo')}
        >
          <Redo2 className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          variant="ghost"
          size="iconSm"
          onClick={openShortcuts}
          aria-label={t('sheets.shortcuts.open')}
          data-testid="shortcuts-button"
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </Button>

        <span className="mx-2 h-5 w-px bg-border" aria-hidden />

        <span className="text-xs text-text-muted" data-testid="sheet-status">
          {sheet.engineStatus === 'ready'
            ? t('sheets.status.evaluated', { done: okCount, total: doc.cells.length })
            : t('sheets.status.engine_starting')}
        </span>
        {errorCount > 0 && (
          <span className="ms-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--status-warn)]">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {t('sheets.status.errors', { count: errorCount })}
          </span>
        )}

        <span className="flex-1" />

        <span className="hidden items-center gap-1.5 text-xs text-text-muted sm:inline-flex">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {t('sheets.status.draft_saved')}
        </span>
      </div>

      {/* Pane selector, below lg only. */}
      <div className="flex gap-1 border-b border-border bg-surface px-4 py-1.5 lg:hidden">
        {(['sheet', 'variables'] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setPane(id)}
            className={cn(
              'min-h-11 rounded-md px-3 text-sm font-medium',
              pane === id ? 'bg-[var(--accent-subtle)] text-accent' : 'text-text-muted',
            )}
          >
            {t(`sheets.tab.${id}`)}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1">
        <div
          className={cn(
            'min-w-0 flex-1 overflow-y-auto p-4 lg:p-6',
            pane === 'variables' && 'hidden lg:block',
          )}
        >
          <div className="mx-auto w-full max-w-3xl rounded-lg border border-border bg-surface p-4 shadow-[var(--shadow-sm)] sm:p-6">
            {doc.cells.map((cell, index) => {
              const shadowed = graph.shadows.get(cell.id)
              return (
                <CellRow
                  key={cell.id}
                  id={cell.id}
                  index={index}
                  result={results.get(cell.id)}
                  selected={selected === cell.id}
                  shadowsCell={shadowed ? doc.cells.findIndex((c) => c.id === shadowed) + 1 : null}
                  onSelect={() => sheet.select(cell.id)}
                  onDelete={() => sheet.remove(cell.id)}
                  onMoveBy={(delta) => moveBy(cell.id, delta)}
                  onDragStart={() => setDrag({ id: cell.id, to: index, allowed: true })}
                  onDragOver={(position) => {
                    if (!drag) return
                    const to = position === 'before' ? index : index + 1
                    if (drag.to === to) return
                    setDrag({ ...drag, to, allowed: sheet.checkMove(drag.id, to).ok })
                  }}
                  onDrop={() => {
                    if (!drag) return
                    if (drag.allowed) sheet.move(drag.id, drag.to)
                    else sheet.moveWithDependencies(drag.id, drag.to)
                    setDrag(null)
                  }}
                  dropHint={
                    drag && drag.to === index ? (drag.allowed ? 'allowed' : 'blocked') : 'none'
                  }
                >
                  {renderBody(cell)}
                </CellRow>
              )
            })}

            <hr className="my-4 border-border" />
            <p className="flex items-start gap-2 text-xs leading-relaxed text-text-muted">
              <AlertTriangle
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--status-warn)]"
                aria-hidden
              />
              {t('sheets.unverified.footer')}
            </p>
          </div>
        </div>

        <aside
          className={cn(
            'w-full shrink-0 border-s border-border bg-surface lg:w-80',
            pane === 'sheet' && 'hidden lg:block',
          )}
        >
          <VariablesPanel doc={doc} graph={graph} results={results} selected={selected} />
        </aside>
      </div>

      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  )
}
